import { Button, Spin } from 'antd';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import type { AsaasCheckoutOpenError, AsaasPaymentSnapshot } from '../asaas';

interface AsaasPaymentContentProps {
  snapshot?: AsaasPaymentSnapshot;
  refreshing: boolean;
  checkoutOpenError?: AsaasCheckoutOpenError;
  canReopen: boolean;
  onRefreshStatus: () => void;
  onContinue: () => void;
  onReopen: () => void;
  onViewBilling?: () => void;
}

function AsaasPaymentContent(props: AsaasPaymentContentProps) {
  const {
    snapshot,
    refreshing,
    checkoutOpenError,
    canReopen,
    onRefreshStatus,
    onContinue,
    onReopen,
    onViewBilling,
  } = props;
  const { t } = useTranslate();
  const phase = snapshot?.phase ?? 'error';
  const busy = phase === 'querying' || phase === 'pending';
  const unrecoverable = phase === 'unrecoverable';

  const message =
    phase === 'failed'
      ? t('translation.支付失败')
      : phase === 'cancelled'
      ? t('market.已取消')
      : phase === 'error'
      ? t('支付结果查询失败，请稍后重试')
      : phase === 'unrecoverable'
      ? t('无法确认本次支付，请查看账单或联系客服')
      : t('market.支付结果查询中...');

  return (
    <Content>
      {busy && <Spin />}
      <Message>{message}</Message>
      {checkoutOpenError && <Tip>{t('收银台未能自动打开，请点击下方按钮继续')}</Tip>}
      {phase === 'confirming' && <Tip>{t('如已完成付款，请勿重复支付')}</Tip>}
      <Actions>
        {checkoutOpenError && canReopen && (
          <Button type="primary" loading={refreshing} onClick={onReopen}>
            {t('market.继续支付')}
          </Button>
        )}
        {(phase === 'error' || phase === 'confirming') && (
          <Button onClick={onRefreshStatus}>{t('刷新支付状态')}</Button>
        )}
        {phase === 'failed' && (
          <Button type="primary" loading={refreshing} onClick={onContinue}>
            {t('market.继续支付')}
          </Button>
        )}
        {phase === 'confirming' && (
          <Button type="link" loading={refreshing} onClick={onContinue}>
            {t('未完成支付？返回收银台')}
          </Button>
        )}
        {(phase === 'cancelled' || unrecoverable) && onViewBilling && (
          <Button onClick={onViewBilling}>{t('查看账单')}</Button>
        )}
      </Actions>
    </Content>
  );
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
`;

const Message = styled.div`
  color: var(--fett-text-primary);
  text-align: center;
`;

const Tip = styled.div`
  color: var(--fett-text-tertiary);
  font-size: 12px;
  text-align: center;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

export default AsaasPaymentContent;
